import React, { useEffect, useRef, useState } from 'react';
import useHydrated from '../hooks/useHydrated';
import './HostedAppDemo.css';

export const VIRGIL_DEMO_URL = 'https://demo.virgil.com.ai/';

function HostedAppDemo({
  variant = 'section',
  showFrame = true,
  showCopy = true,
  eyebrow = 'Live Studio Demo',
  title = 'Step inside the Virgil studio',
  description = 'The real Virgil workspace, running live. Start a scene and write along, then open it full screen whenever you are ready.',
  ctaLabel = 'Open the studio',
  className = '',
}) {
  const rootRef = useRef(null);
  const hydrated = useHydrated();
  const isMobileVariant = variant === 'mobile';
  const [isRevealed, setIsRevealed] = useState(isMobileVariant);
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);

  // Once the reader scrolls the live demo mostly out of view, hand touch
  // scrolling back to the page so it cannot trap them on the way back.
  useEffect(() => {
    if (!isMobileVariant || !isInteractive) return undefined;
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.intersectionRatio < 0.35) setIsInteractive(false);
      },
      { threshold: [0, 0.35] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobileVariant, isInteractive]);

  useEffect(() => {
    if (isMobileVariant) return undefined;

    const node = rootRef.current;
    if (!node) return undefined;

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRevealed(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -12% 0px', threshold: 0.18 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobileVariant]);

  const demoUrl = isMobileVariant
    ? `${VIRGIL_DEMO_URL}?shell=mobile&embed=1`
    : VIRGIL_DEMO_URL;

  const classes = [
    'hosted-app-demo',
    `hosted-app-demo--${variant}`,
    !showFrame ? 'hosted-app-demo--copy-only' : '',
    !showCopy ? 'hosted-app-demo--frame-only' : '',
    isRevealed ? 'is-revealed' : '',
    isFrameLoaded ? 'is-frame-loaded' : '',
    isInteractive ? 'is-interactive' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} ref={rootRef}>
      {showCopy && (
        <div className="hosted-app-demo-copy">
          <span className="hosted-app-demo-eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p>{description}</p>
          <a
            className="hosted-app-demo-link"
            href={VIRGIL_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ctaLabel}
            <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      )}

      {showFrame && (
        <div className="hosted-app-demo-stage" aria-label="Embedded Virgil app demo">
          <div className="hosted-app-demo-stage-art" aria-hidden="true" />
          <div className="hosted-app-demo-stage-veil" aria-hidden="true" />
          <div className="hosted-app-demo-stage-glow" aria-hidden="true" />

          <div className="hosted-app-demo-window">
            <div className="hosted-app-demo-window-bar">
              {variant !== 'mobile' && (
                <div className="hosted-app-demo-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              )}
              <div className="hosted-app-demo-wordmark">Virgil Studio</div>
              {variant !== 'mobile' && (
                <a
                  className="hosted-app-demo-popout"
                  href={VIRGIL_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open<span aria-hidden="true"> &rarr;</span>
                </a>
              )}
              {/* On phones the control lives in the window bar, clear of the
                  demo's own composer at the bottom of the screen. */}
              {isMobileVariant && isFrameLoaded && (
                <button
                  type="button"
                  className="hosted-app-demo-mobile-toggle"
                  onClick={() => setIsInteractive((current) => !current)}
                  aria-pressed={isInteractive}
                >
                  {isInteractive ? 'Done' : 'Try it'}
                </button>
              )}
            </div>
            <div className="hosted-app-demo-screen">
              {/* The phone iframe waits for hydration: a load event that
                  fires before React attaches would leave it hidden. */}
              {((isMobileVariant && hydrated) || (!isMobileVariant && isRevealed)) && (
                <iframe
                  title="Virgil hosted app demo"
                  src={demoUrl}
                  loading={isMobileVariant ? 'eager' : 'lazy'}
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="clipboard-read; clipboard-write"
                  onLoad={() => setIsFrameLoaded(true)}
                  tabIndex={isMobileVariant && !isInteractive ? -1 : 0}
                />
              )}
              {/* Until the reader opts in, a tap on the demo starts it and a
                  swipe keeps scrolling the page. */}
              {isMobileVariant && isFrameLoaded && !isInteractive && (
                <div
                  className="hosted-app-demo-shield"
                  aria-hidden="true"
                  onClick={() => setIsInteractive(true)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HostedAppDemo;
